import React from "react";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { FaFileExcel, FaPrint } from "react-icons/fa";

export default function TableActions({
  tableRef,
  searchValue,
  onSearchChange,
  showSearch = true,
  showPrint = false,
  showExcel = false,
  excelFileName = "Table_Report",
}) {
  /* ===== PRINT ===== */
  const handlePrint = useReactToPrint({
    contentRef: tableRef,
    documentTitle: excelFileName,
  });

  /* ===== SEARCH (HTML TABLE FILTER – ALL COLUMNS) ===== */
  const handleSearch = (value) => {
    onSearchChange(value);

    const table = tableRef.current;
    if (!table) return;

    const search = value.toLowerCase();
    const rows = table.querySelectorAll("tbody tr");

    rows.forEach((row) => {
      const cells = row.querySelectorAll("td");

      const rowText = Array.from(cells)
        .map((td) => td.textContent.trim().toLowerCase())
        .join(" ");

      row.style.display = rowText.includes(search) ? "" : "none";
    });
  };

  /* ===== EXCEL EXPORT (HTML TABLE → XLSX) ===== */
  const handleExcelExport = () => {
    const table = tableRef.current;
    if (!table) return;

    const worksheet = XLSX.utils.table_to_sheet(table, { raw: true });

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    /* ===========================
     ✅ CENTER ALIGN HEADER ROWS
     (You have 3 header rows)
  ============================ */
    const HEADER_ROWS = 3;

    for (let R = 0; R < HEADER_ROWS; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];

        if (cell) {
          cell.s = {
            alignment: {
              horizontal: "center",
              vertical: "center",
              wrapText: true,
            },
            font: {
              bold: true,
            },
          };
        }
      }
    }

    /* ===========================
     ✅ AUTO COLUMN WIDTH
  ============================ */
    const colWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 12;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell?.v) {
          maxWidth = Math.max(maxWidth, cell.v.toString().length + 2);
        }
      }
      colWidths.push({ wch: maxWidth });
    }
    worksheet["!cols"] = colWidths;

    /* ===========================
     ✅ FREEZE HEADER
  ============================ */
    worksheet["!freeze"] = { xSplit: 0, ySplit: HEADER_ROWS };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Report");

    XLSX.writeFile(workbook, `${excelFileName}.xlsx`);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      {/* SEARCH */}
      {showSearch && (
        <input
          type="text"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search..."
          style={{ padding: "5px 10px", width: "200px" }}
        />
      )}

      {/* ACTION BUTTONS */}
      <div style={{ display: "flex", gap: "10px" }}>
        {showExcel && (
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={handleExcelExport}
            title="Export to Excel"
          >
            <FaFileExcel size={18} /> Excel
          </button>
        )}

        {showPrint && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handlePrint}
            title="Print"
          >
            <FaPrint size={18} /> Print
          </button>
        )}
      </div>
    </div>
  );
}
